<script lang="ts">
export default {
  name: 'TowerContextMenu',
}
</script>

<script setup lang="ts">
import { inject, computed } from 'vue';
import { StoreMutationType } from '@/types/StoreMutationType';
import { Store } from 'vuex';
import { AppState, Commit } from '@/store';
import { LabelFormat } from '@/views/tower/v2/objects/policy_rect/PolicyRect';
import { CHANGE_OBJECT_LAYER, ChangeObjectLayerOptions } from '@/store/CustomEvents';

const props = defineProps<{
  show: boolean,
  left?: string,
  top?: string,
  activeObjectId?: string,
  objectType?: string,
}>();

const store = inject<Store<AppState>>('store');

const gridModeEnabled = computed(() => {
  return store?.state.towerCanvasState.gridModeEnabled;
});

const snapToGridEnabled = computed(() => {
  return store?.state.towerCanvasState.snapToModeEnabled;
});

const isPrimaryPolicyRect = computed(() => {
  const activeObject = store?.state.towerCanvasState.canvasObjects.find((o) => o.id === props.activeObjectId);
  if (activeObject instanceof fabric.PolicyRect) {
    return activeObject?.isPrimary ?? false;
  }
  return false;
});

const retentionEnabled = computed(() => {
  const activeObject = store?.state.towerCanvasState.canvasObjects.find((o) => o.id === props.activeObjectId);
  if (activeObject instanceof fabric.PolicyRect) {
    return activeObject?.retentionEnabled ?? false;
  }
  return false;
});

const isPolicyLine = computed(() => {
  const activeObject = store?.state.towerCanvasState.canvasObjects.find((o) => o.id === props.activeObjectId);
  if (activeObject instanceof fabric.PolicyRect) {
    return activeObject?.getPolicyData().isChildPolicy ?? false;
  }
  return false;
});

const towerScaleMode = computed(() => {
  const activeObject = store?.state.towerCanvasState.canvasObjects.find((o) => o.id === props.activeObjectId);
  if (activeObject instanceof fabric.TowerScale) {
    return activeObject.scaleMode;
  }
  return '';
});

const labelFormat = computed(() => {
  const activeObject = store?.state.towerCanvasState.canvasObjects.find((o) => o.id === props.activeObjectId);
  if (activeObject instanceof fabric.PolicyRect) {
    return activeObject?.labelFormat ?? LabelFormat.Default;
  }
  return LabelFormat.Default;
})

enum Action {
  ShowGrid,
  ToggleSnapTo,
  EditPolicy,
  EditDisplayOptions,
  Delete,
  Duplicate,
  ToggleRetention,
  Dynamic,
  Aggregate,
  Occurrence,
  LayerMove,
  ToggleLabelFormat,
}

const enabledActions = new Map<string, Set<Action>>([
  ['custom-policy-rect', new Set<Action>([Action.EditPolicy, Action.ToggleRetention, Action.Delete, Action.Duplicate, Action.ToggleLabelFormat])],
  ['policy-rect', new Set<Action>([Action.EditPolicy, Action.Delete, Action.ToggleRetention, Action.ToggleLabelFormat])],
  ['legend-rect', new Set<Action>([Action.Delete, Action.EditDisplayOptions])],
  ['triangle', new Set<Action>([Action.Delete, Action.Duplicate])],
  ['labeled-rect', new Set<Action>([Action.Delete, Action.Duplicate])],
  ['i-text', new Set<Action>([Action.Delete, Action.Duplicate])],
  ['activeSelection', new Set<Action>([Action.Delete, Action.Duplicate])],
  ['canvas', new Set<Action>([Action.ShowGrid, Action.ToggleSnapTo])],
  ['tower-scale', new Set<Action>([Action.Dynamic, Action.Aggregate, Action.Occurrence])],
]);

const isActionEnabled = (action: Action): boolean => {
  if (action === Action.LayerMove) {
    return !!props.objectType;
  }

  return enabledActions.get(props.objectType ?? 'canvas')?.has(action) ?? false;
}

const toggleOption = (optionName: string): void => {
  const activeObject = store?.state.towerCanvasState.canvasObjects.find((o) => o.id === props.activeObjectId);
  switch (optionName) {
    case 'grid':
      store?.commit(StoreMutationType.SET_GRID_MODE, !store?.state.towerCanvasState.gridModeEnabled);
      break;
    case 'snapToGrid':
      store?.commit(StoreMutationType.SET_SNAP_TO_MODE, !store?.state.towerCanvasState.snapToModeEnabled);
      break;
    case 'editPolicy':
      Commit(StoreMutationType.SET_TOWER_EDITOR_EXPANDED, true);
      break;
    case 'duplicate':
      Commit(StoreMutationType.DUPLICATE_FABRIC_OBJECT, true);
      break;
    case 'delete':
      Commit(StoreMutationType.DELETE_FABRIC_OBJECT, true);
      break;
    case 'editDisplayOptions':
      Commit(StoreMutationType.EDIT_TOWER_DISPLAY_OPTIONS, {
        show: true,
      });
      break;
    case 'showRetention': {
      if (activeObject instanceof fabric.PolicyRect) {
        activeObject?.toggleRetention();
      }
      break;
    }
    case 'toggleLabelFormat': {
      if (activeObject instanceof fabric.PolicyRect) {
        activeObject?.toggleLabelFormat();
      }
      break;
    }
    case 'dynamic':
    case 'aggregate':
    case 'occurrence':
      if (activeObject && activeObject instanceof fabric.TowerScale) {
        activeObject.setMode(optionName);
      }
      break;
    case 'send-backward':
      dispatchEvent(new CustomEvent<ChangeObjectLayerOptions>(CHANGE_OBJECT_LAYER, {detail: 'send-backward'}));
      break;
    case 'send-to-back':
      dispatchEvent(new CustomEvent<ChangeObjectLayerOptions>(CHANGE_OBJECT_LAYER, {detail: 'send-to-back'}));
      break;
    case 'bring-forward':
      dispatchEvent(new CustomEvent<ChangeObjectLayerOptions>(CHANGE_OBJECT_LAYER, {detail: 'bring-forward'}));
      break;
    case 'bring-to-front':
      dispatchEvent(new CustomEvent<ChangeObjectLayerOptions>(CHANGE_OBJECT_LAYER, {detail: 'bring-to-front'}));
      break;
  }

  // hide the context menu
  store?.commit(StoreMutationType.SET_TOWER_CONTEXT_MENU_STATE, { show: false });
};
</script>

<template>
  <div v-if="show" class="popover" :style="{ left, top }">
    <ul class="context-menu">
      <li v-if="isActionEnabled(Action.ShowGrid)">
        <button class="context-menu-option" :class="{ checkmark: gridModeEnabled }" @click="toggleOption('grid')">
          Show Grid
        </button>
      </li>
      <li v-if="isActionEnabled(Action.ToggleSnapTo)">
        <button class="context-menu-option" :class="{ checkmark: snapToGridEnabled }" @click="toggleOption('snapToGrid')">
          Snap To Grid
        </button>
      </li>
      <li v-if="isActionEnabled(Action.EditPolicy)">
        <button class="context-menu-option" @click="toggleOption('editPolicy')">
          {{ isPolicyLine ? 'Edit Line' : 'Edit Policy'}}
        </button>
      </li>
      <li v-if="isActionEnabled(Action.ToggleRetention) && isPrimaryPolicyRect">
        <button class="context-menu-option" :class="{ checkmark: retentionEnabled }" @click="toggleOption('showRetention')">
          Show Retention
        </button>
      </li>
      <li v-if="isActionEnabled(Action.ToggleLabelFormat)">
        <button class="context-menu-option" @click="toggleOption('toggleLabelFormat')">
          {{ labelFormat === LabelFormat.Default ? 'Inline Label' : 'Stacked Label'}}
        </button>
      </li>
      <li v-if="isActionEnabled(Action.EditDisplayOptions)">
        <button class="context-menu-option" @click="toggleOption('editDisplayOptions')">
          Edit Display Options
        </button>
      </li>
      <li v-if="isActionEnabled(Action.Duplicate)">
        <button class="context-menu-option" @click="toggleOption('duplicate')">
          Duplicate
        </button>
      </li>
      <li v-if="isActionEnabled(Action.Delete)">
        <button class="context-menu-option" @click="toggleOption('delete')">
          Delete
        </button>
      </li>
      <li v-if="isActionEnabled(Action.Dynamic)">
        <button class="context-menu-option" @click="toggleOption('dynamic')" :class="{ checkmark: towerScaleMode === 'dynamic' }">
          Dynamic
        </button>
      </li>
      <li v-if="isActionEnabled(Action.Aggregate)">
        <button class="context-menu-option" @click="toggleOption('aggregate')" :class="{ checkmark: towerScaleMode === 'aggregate' }">
          Aggregate
        </button>
      </li>
      <li v-if="isActionEnabled(Action.Occurrence)">
        <button class="context-menu-option" @click="toggleOption('occurrence')" :class="{ checkmark: towerScaleMode === 'occurrence' }">
          Occurrence
        </button>
      </li>

      <li v-if="isActionEnabled(Action.LayerMove)" class="py-1">
        <hr/>
      </li>

      <li v-if="isActionEnabled(Action.LayerMove)" >
        <button class="context-menu-option" @click="toggleOption('send-backward')">
          Send backward
        </button>
      </li>
      <li v-if="isActionEnabled(Action.LayerMove)" >
        <button class="context-menu-option" @click="toggleOption('bring-forward')">
          Bring forward
        </button>
      </li>
      <li v-if="isActionEnabled(Action.LayerMove)" >
        <button class="context-menu-option" @click="toggleOption('send-to-back')">
          Send to back
        </button>
      </li>
      <li v-if="isActionEnabled(Action.LayerMove)" >
        <button class="context-menu-option" @click="toggleOption('bring-to-front')">
          Bring to front
        </button>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.context-menu {
  background-color: var(--tower-v2-context-menu-bg-color);
  opacity: 0.95;
  list-style: none;
  padding: 0.3rem 0;
  border-radius: 3px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
}

.context-menu-option {
  color: #ced4da;
  padding: 0.25rem 1rem 0.25rem 1.15rem;
  transition: background-color 0.15s;
  width: 100%;
  text-align: left;
}

.context-menu-option:hover {
  background-color: var(--tower-v2-context-menu-bg-color-hover);
  transition: background-color 0.15s;
}

.context-menu-option.checkmark::before {
  position: absolute;
  left: 6px;
  content: '✓';
}
</style>

<style>
.popover {
  position: absolute;
  z-index: 10;
}
</style>
